import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Building2, User, Image, Upload, Trash2, Save, Clock, ChevronDown, Check, FileText } from 'lucide-react';
import Chart from 'chart.js/auto';
import QuillEditor from '../QuillEditor';
import HtmlImportButton from '../HtmlImportButton';
import ChartBuilderButton from '../ChartBuilderButton';
import AttestModal from '../modals/AttestModal';
import SchedulePicker from '../SchedulePicker';
import { useTheme } from '../../ThemeContext';


export default function EditorView({ 
  setView, 
  currentPost, 
  setCurrentPost,
  connectedWallet,
  authors,
  setAuthors,
  organizations,
  supabase,
}) {
  const { t, isDark } = useTheme();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Life');
  const [selectedAuthorId, setSelectedAuthorId] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  
  // Entity selection: author or organization
  const [entityType, setEntityType] = useState('author');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgSeats, setOrgSeats] = useState({});
  
  const [showNewAuthorModal, setShowNewAuthorModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showLinkAuthorModal, setShowLinkAuthorModal] = useState(false);
  const [showAttestModal, setShowAttestModal] = useState(false);
  const [pendingPostData, setPendingPostData] = useState(null);
  const [pendingEntity, setPendingEntity] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [linkingOrgId, setLinkingOrgId] = useState(null);
  const [newAuthorName, setNewAuthorName] = useState('');
  const [newAuthorBio, setNewAuthorBio] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);

  // Schedule state
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null); // timestamp in ms
  
  // Draft state
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftToast, setDraftToast] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [showDraftsPanel, setShowDraftsPanel] = useState(false);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const draftsPanelRef = useRef(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  // Image upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadMode, setUploadMode] = useState('featured');
  const quillRef = useRef(null);
  const featuredImageRef = useRef(null);
  const inlineImageRef = useRef(null);

  // HTML Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importHtml, setImportHtml] = useState('');
  const [importing, setImporting] = useState(false);

  // Chart Builder modal state
  const [showChartModal, setShowChartModal] = useState(false);
  const [chartType, setChartType] = useState('bar');
  const [chartTitle, setChartTitle] = useState('');
  const [chartLabels, setChartLabels] = useState(['Jan', 'Feb', 'Mar', 'Apr', 'May']);
  const [chartValues, setChartValues] = useState([12, 19, 8, 14, 22]);
  const [chartColor, setChartColor] = useState('#3D2412');
  const chartCanvasRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const categories = [
    'Art', 'Business', 'Culture', 'Design', 'Economics', 'Education',
    'Entertainment', 'Environment', 'Fashion', 'Finance', 'Food', 'Gaming',
    'Health', 'History', 'Humor', 'Life', 'Literature', 'Music', 'News',
    'Opinion', 'Philosophy', 'Photography', 'Poetry', 'Politics', 'Psychology',
    'Science', 'Society', 'Sports', 'Startups', 'Style', 'Technology',
    'Travel', 'True Crime', 'Wellness', 'Work', 'Writing', 'Other'
  ];

  const filteredCategories = categories.filter(cat =>
    cat.toLowerCase().includes(categorySearch.toLowerCase())
  );

  useEffect(() => {
    if (organizations && organizations.length > 0 && connectedWallet) {
      loadOrgSeats();
    }
  }, [organizations, connectedWallet]);

  const loadOrgSeats = async () => {
    try {
      const orgIds = organizations.map(o => o.id);
      const { data, error } = await supabase
        .from('org_seats')
        .select('org_id, author_id, role')
        .eq('wallet_address', connectedWallet)
        .in('org_id', orgIds);

      if (!error && data) {
        const seatsMap = {};
        data.forEach(seat => {
          seatsMap[seat.org_id] = { author_id: seat.author_id, role: seat.role };
        });
        setOrgSeats(seatsMap);
      }
    } catch (err) {
      console.error('Error loading org seats:', err);
    }
  };

  useEffect(() => {
    if (currentPost) {
      setTitle(currentPost.title || '');
      setSubtitle(currentPost.subtitle || '');
      setContent(currentPost.content || '');
      setCategory(currentPost.category || 'Life');
      setFeaturedImage(currentPost.featured_image_url || '');
      
      if (currentPost.entity_type === 'organization') {
        setEntityType('organization');
        setSelectedOrgId(currentPost.entity_id || '');
        setSelectedAuthorId(currentPost.written_by_author_id || '');
      } else {
        setEntityType('author');
        setSelectedAuthorId(currentPost.author_id || '');
      }
      // Restore schedule state if post has a future scheduled_at
      if (currentPost.scheduled_at && currentPost.scheduled_at > Date.now()) {
        setScheduleOn(true);
        setScheduledAt(currentPost.scheduled_at);
      }
    } else {
      if (authors.length > 0) {
        setSelectedAuthorId(authors[0].id);
      }
    }
  }, [currentPost, authors]);

  const resetEditor = () => {
    setTitle('');
    setSubtitle('');
    setContent('');
    setCategory('Life');
    setFeaturedImage('');
    setCurrentPost(null);
    setEntityType('author');
    setSelectedOrgId('');
    setScheduleOn(false);
    setScheduledAt(null);
    if (authors.length > 0) {
      setSelectedAuthorId(authors[0].id);
    }
  };

  // ==========================================
  // IMAGE UPLOAD
  // ==========================================
  const uploadImage = async (file, mode = 'featured') => {
    if (!file) return null;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a JPEG, PNG, GIF, or WebP image');
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB');
      return null;
    }
    
    setUploading(true);
    setUploadError('');
    
    try {
      const timestamp = Date.now();
      const ext = file.name.split('.').pop();
      const filename = `${connectedWallet.slice(0, 12)}_${timestamp}.${ext}`;
      
      const { data, error } = await supabase.storage
        .from('images')
        .upload(filename, file, { cacheControl: '3600', upsert: false });
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('images')
        .getPublicUrl(filename);
      
      const publicUrl = urlData.publicUrl;
      if (mode === 'featured') setFeaturedImage(publicUrl);
      
      setUploading(false);
      return publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Upload failed. Please try again.');
      setUploading(false);
      return null;
    }
  };

  const handleFeaturedImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadImage(file, 'featured');
  };

  const handleInlineImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await uploadImage(file, 'inline');
      if (url) {
        const editor = quillRef.current?.getEditor();
        if (editor) {
          editor.chain().focus().setImage({ src: url, alt: 'Uploaded image' }).run();
        }
        setShowImageUploadModal(false);
      }
    }
  };

  const removeFeaturedImage = () => {
    setFeaturedImage('');
    if (featuredImageRef.current) featuredImageRef.current.value = '';
  };

  // ==========================================
  // BUILD POST DATA (shared by save + draft)
  // ==========================================
  const buildPostData = () => {
    const selectedAuthor = authors.find(a => a.id === selectedAuthorId);
    const selectedOrg = organizations?.find(o => o.id === selectedOrgId);

    const plainText = content.replace(/<[^>]*>/g, '');
    const wordCount = plainText.trim().split(/\s+/).length;
    const readTime = Math.ceil(wordCount / 200);

    let postData = {
      title: title.trim(),
      subtitle: subtitle.trim(),
      content,
      category,
      wallet_address: connectedWallet,
      read_time: readTime,
      updated_at: Date.now(),
      featured_image_url: featuredImage || null,
      scheduled_at: scheduleOn && scheduledAt ? scheduledAt : null,
    };

    if (entityType === 'organization') {
      const seat = orgSeats[selectedOrgId];
      postData = {
        ...postData,
        author: selectedOrg.name,
        author_id: null,
        entity_type: 'organization',
        entity_id: selectedOrgId,
        written_by_author_id: seat.author_id
      };
    } else {
      postData = {
        ...postData,
        author: selectedAuthor?.name || '',
        author_id: selectedAuthorId,
        entity_type: 'individual',
        entity_id: selectedAuthorId,
        written_by_author_id: null
      };
    }

    return postData;
  };

  // ==========================================
  // SAVE DRAFT
  // ==========================================
  const saveDraft = async () => {
    if (!title.trim()) {
      alert('Add a title before saving a draft');
      return;
    }

    setSavingDraft(true);

    try {
      const postData = buildPostData();
      postData.status = 'draft';

      if (currentPost) {
        // Update existing post/draft
        const { error } = await supabase
          .from('posts')
          .update(postData)
          .eq('id', currentPost.id);

        if (error) throw error;
        setCurrentPost({ ...currentPost, ...postData });
      } else {
        // Create new draft
        postData.id = crypto.randomUUID();
        postData.timestamp = Date.now();
        postData.views = 0;
        postData.likes = 0;

        const { data, error } = await supabase
          .from('posts')
          .insert([postData])
          .select()
          .single();

        if (error) throw error;
        setCurrentPost(data);
      }

      setSavingDraft(false);
      setDraftToast(true);
      setTimeout(() => setDraftToast(false), 2000);
    } catch (error) {
      console.error('Error saving draft:', error);
      setSavingDraft(false);
      alert('Error saving draft. Please try again.');
    }
  };

  // ==========================================
  // DRAFTS PANEL
  // ==========================================
  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, updated_at, category, author')
        .eq('wallet_address', connectedWallet)
        .eq('status', 'draft')
        .order('updated_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setDrafts(data || []);
    } catch (err) {
      console.error('Error fetching drafts:', err);
    }
    setLoadingDrafts(false);
  };

  const loadDraft = async (draftId) => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('id', draftId)
        .single();

      if (error) throw error;
      setCurrentPost(data);
      setTitle(data.title || '');
      setSubtitle(data.subtitle || '');
      setContent(data.content || '');
      setCategory(data.category || 'Life');
      setFeaturedImage(data.featured_image_url || '');
      if (data.entity_type === 'organization') {
        setEntityType('organization');
        setSelectedOrgId(data.entity_id || '');
      } else {
        setEntityType('author');
        setSelectedAuthorId(data.author_id || data.entity_id || '');
      }
      if (data.scheduled_at) {
        setScheduleOn(true);
        setScheduledAt(data.scheduled_at);
      }
      setShowDraftsPanel(false);
    } catch (err) {
      console.error('Error loading draft:', err);
    }
  };

  const deleteDraft = async (draftId, e) => {
    e.stopPropagation();
    try {
      await supabase.from('posts').delete().eq('id', draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      if (currentPost?.id === draftId) setCurrentPost(null);
    } catch (err) {
      console.error('Error deleting draft:', err);
    }
  };

  const toggleDraftsPanel = () => {
    const next = !showDraftsPanel;
    setShowDraftsPanel(next);
    if (next) fetchDrafts();
  };

  // ==========================================
  // PUBLISH (with attestation)
  // ==========================================
  const savePost = async () => {
    if (!title.trim() || !content.trim()) {
      alert('Title and content are required');
      return;
    }

    if (entityType === 'author' && !selectedAuthorId) {
      alert('Please select an author');
      return;
    }

    if (entityType === 'organization') {
      if (!selectedOrgId) {
        alert('Please select an organization');
        return;
      }
      const seat = orgSeats[selectedOrgId];
      if (!seat?.author_id) {
        alert('Please link an author to this organization first');
        return;
      }
    }

    // Validate schedule
    if (scheduleOn && !scheduledAt) {
      alert('Please select a date and time for scheduling');
      return;
    }
    if (scheduleOn && scheduledAt) {
      if (scheduledAt <= Date.now() + 5 * 60 * 1000) {
        alert('Scheduled time must be at least 5 minutes in the future');
        return;
      }
    }

    const postData = buildPostData();
    postData.status = scheduleOn ? 'scheduled' : 'published';

    try {
      if (currentPost && currentPost.status !== 'draft') {
        // UPDATE existing published post — no attestation needed
        const { error } = await supabase
          .from('posts')
          .update(postData)
          .eq('id', currentPost.id);

        if (error) throw error;

        if (postData.scheduled_at && postData.scheduled_at > Date.now()) {
          const d = new Date(postData.scheduled_at);
          const formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          alert(`Scheduled for ${formatted}. You can manage it in Author Settings → Scheduled.`);
        } else {
          alert('Post updated successfully!');
        }

        const updatedPost = { ...currentPost, ...postData };
        setCurrentPost(updatedPost);
        setView('reader');
        window.history.pushState({}, '', `?post=${currentPost.id}`);
      } else {
        // NEW post or DRAFT → publish — attest FIRST, then save
        if (currentPost && currentPost.status === 'draft') {
          // Publishing a draft — use existing id
          postData.id = currentPost.id;
          postData.timestamp = currentPost.timestamp || Date.now();
          postData.views = currentPost.views || 0;
          postData.likes = currentPost.likes || 0;
        } else {
          postData.id = crypto.randomUUID();
          postData.timestamp = Date.now();
          postData.views = 0;
          postData.likes = 0;
        }

        const entityForAttest = {
          id: entityType === 'organization' ? selectedOrgId : selectedAuthorId,
          type: entityType === 'organization' ? 'organization' : 'individual'
        };

        setPendingPostData(postData);
        setPendingEntity(entityForAttest);
        setShowAttestModal(true);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post. Please try again.');
    }
  };

  const handleAttestSuccess = async (proof) => {
    setShowAttestModal(false);
    setPublishing(true);

    try {
      const postWithProof = {
        ...pendingPostData,
        attest_tx_hash: (proof.txHash || '').replace(/^["']|["']$/g, ''),
        attest_content_hash: proof.contentHash,
        attest_timestamp: proof.timestamp
      };

      let result;
      if (currentPost && currentPost.status === 'draft') {
        // Update existing draft → published
        const { id, ...updateData } = postWithProof;
        const { data, error } = await supabase
          .from('posts')
          .update(updateData)
          .eq('id', currentPost.id)
          .select()
          .single();
        if (error) throw error;
        result = data;
      } else {
        // Insert new post
        const { data, error } = await supabase
          .from('posts')
          .insert([postWithProof])
          .select()
          .single();
        if (error) throw error;
        result = data;
      }

      setCurrentPost(result);
      setPendingPostData(null);
      setPendingEntity(null);
      setPublishing(false);

      if (result.scheduled_at && result.scheduled_at > Date.now()) {
        const d = new Date(result.scheduled_at);
        const formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        alert(`Scheduled for ${formatted}. You can manage it in Author Settings → Scheduled.`);
      }

      setView('reader');
      window.history.pushState({}, '', `?post=${result.id}`);
    } catch (error) {
      console.error('Error saving post after attestation:', error);
      setPublishing(false);
      alert('Attestation succeeded but post failed to save. Your tx hash: ' + proof.txHash + '. Please try publishing again.');
    }
  };

  const handleAttestCancel = () => {
    setShowAttestModal(false);
    setPendingPostData(null);
    setPendingEntity(null);
  };

  const handleCancel = () => {
    const hasContent = title.trim() || content.replace(/<[^>]*>/g, '').trim();
    const isDraftPost = currentPost?.status === 'draft';
    const isPublished = currentPost && currentPost.status !== 'draft';

    if (isPublished) {
      // Published post — just go back, no prompt
      setView('reader');
      window.history.pushState({}, '', `?post=${currentPost.id}`);
      return;
    }

    if (isDraftPost || hasContent) {
      setShowCancelConfirm(true);
      return;
    }

    // Empty new post — just leave
    setView('home');
    window.history.pushState({}, '', '/');
    resetEditor();
  };

  const confirmDiscard = () => {
    setShowCancelConfirm(false);
    const isDraftPost = currentPost?.status === 'draft';

    if (isDraftPost) {
      // Discard draft = delete it
      supabase.from('posts').delete().eq('id', currentPost.id).then(() => {});
    }

    setView('home');
    window.history.pushState({}, '', '/');
    resetEditor();
  };

  const confirmSaveDraft = async () => {
    setShowCancelConfirm(false);
    await saveDraft();
    setView('home');
    window.history.pushState({}, '', '/');
    resetEditor();
  };

  const createNewAuthor = async () => {
    if (!newAuthorName.trim()) {
      alert('Author name is required');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('authors')
        .insert([{
          name: newAuthorName.trim(),
          bio: newAuthorBio.trim(),
          wallet_address: connectedWallet
        }])
        .select()
        .single();
      if (error) throw error;
      setAuthors([...authors, data]);
      setSelectedAuthorId(data.id);
      setNewAuthorName('');
      setNewAuthorBio('');
      setShowNewAuthorModal(false);
    } catch (error) {
      console.error('Error creating author:', error);
      alert('Error creating author. Please try again.');
    }
  };

  const linkAuthorToOrg = async (authorId) => {
    if (!linkingOrgId || !authorId) return;
    try {
      const { error } = await supabase
        .from('org_seats')
        .update({ author_id: authorId })
        .eq('org_id', linkingOrgId)
        .eq('wallet_address', connectedWallet);
      if (error) throw error;
      setOrgSeats(prev => ({
        ...prev,
        [linkingOrgId]: { ...prev[linkingOrgId], author_id: authorId }
      }));
      setShowLinkAuthorModal(false);
      setLinkingOrgId(null);
      setSelectedOrgId(linkingOrgId);
      setSelectedAuthorId(authorId);
    } catch (error) {
      console.error('Error linking author:', error);
      alert('Error linking author. Please try again.');
    }
  };

  const handleEntitySelect = (value) => {
    if (value === 'new') {
      setShowNewAuthorModal(true);
      return;
    }
    if (value.startsWith('org_')) {
      const orgId = value.replace('org_', '');
      const seat = orgSeats[orgId];
      if (!seat?.author_id) {
        setLinkingOrgId(orgId);
        setShowLinkAuthorModal(true);
        return;
      }
      setEntityType('organization');
      setSelectedOrgId(orgId);
      setSelectedAuthorId(seat.author_id);
    } else {
      setEntityType('author');
      setSelectedOrgId('');
      setSelectedAuthorId(value);
    }
  };

  // ==========================================
  // HTML IMPORT
  // ==========================================
  const handleImportHtml = () => {
    if (!importHtml.trim()) return;
    setImporting(true);

    const finishImport = (html) => {
      try {
        quillRef.current?.setRawHTML(html);
      } catch (e) {
        console.error('[IMPORT] setRawHTML failed:', e);
        quillRef.current?.setContent(html);
      }
      setImportHtml('');
      setShowImportModal(false);
      setImporting(false);
      setTimeout(() => {
        const html = quillRef.current?.debugHTML?.();
        console.log('[DEBUG] TipTap stored HTML (first 2000 chars):', html?.slice(0, 2000));
      }, 200);
    };

    try {
      const doc = new DOMParser().parseFromString(importHtml, 'text/html');
      const svgs = doc.querySelectorAll('svg');

      if (svgs.length === 0) {
        finishImport(doc.body.innerHTML);
        return;
      }

      // SVG conversion with 3-second timeout
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        console.warn('[IMPORT] SVG conversion timed out after 3s, importing without conversion');
        finishImport(importHtml);
      }, 3000);

      svgs.forEach((svgEl) => {
        if (timedOut) return;
        try {
          let width = 600;
          let height = 400;
          const viewBox = svgEl.getAttribute('viewBox');
          if (viewBox) {
            const parts = viewBox.split(/[\s,]+/);
            if (parts.length >= 4) {
              width = parseFloat(parts[2]) || 600;
              height = parseFloat(parts[3]) || 400;
            }
          } else {
            width = parseFloat(svgEl.getAttribute('width')) || 600;
            height = parseFloat(svgEl.getAttribute('height')) || 400;
          }

          if (!svgEl.getAttribute('xmlns')) {
            svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          }
          if (!svgEl.getAttribute('width')) svgEl.setAttribute('width', String(width));
          if (!svgEl.getAttribute('height')) svgEl.setAttribute('height', String(height));

          const svgString = new XMLSerializer().serializeToString(svgEl);
          const b64 = btoa(encodeURIComponent(svgString).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16))));
          const dataUrl = 'data:image/svg+xml;base64,' + b64;

          const img = document.createElement('img');
          img.src = dataUrl;
          img.setAttribute('width', String(width));
          img.setAttribute('height', String(height));

          svgEl.parentNode.replaceChild(img, svgEl);
        } catch (svgErr) {
          console.error('[IMPORT] SVG conversion failed, leaving in place:', svgErr);
        }
      });

      if (!timedOut) {
        clearTimeout(timeout);
        finishImport(doc.body.innerHTML);
      }
    } catch (err) {
      console.error('[IMPORT] Top-level error:', err);
      finishImport(importHtml);
    }
  };

  // ==========================================
  // CHART BUILDER
  // ==========================================
  useEffect(() => {
    if (!chartCanvasRef.current || !showChartModal) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const isPieType = chartType === 'pie' || chartType === 'doughnut';

    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const bgColors = isPieType
      ? chartValues.map((_, i) => hexToRgba(chartColor, 0.4 + 0.6 * (1 - i / Math.max(chartValues.length - 1, 1))))
      : chartColor;

    chartInstanceRef.current = new Chart(chartCanvasRef.current, {
      type: chartType,
      data: {
        labels: chartLabels,
        datasets: [{
          label: chartTitle || 'Data',
          data: chartValues,
          backgroundColor: chartType === 'line' ? 'transparent' : bgColors,
          borderColor: isPieType ? '#fff' : chartColor,
          borderWidth: chartType === 'line' ? 2 : isPieType ? 2 : 1,
          tension: 0.3,
          pointBackgroundColor: chartColor,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          title: {
            display: !!chartTitle,
            text: chartTitle,
            font: { family: 'Poppins, sans-serif', size: 16 }
          },
          legend: { display: false }
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartType, chartTitle, chartLabels, chartValues, chartColor, showChartModal]);

  const handleInsertChart = () => {
    const canvas = chartCanvasRef.current;
    if (!canvas) return;
    const dataURL = canvas.toDataURL('image/png');
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.chain().focus().setImage({ src: dataURL }).run();
      setShowChartModal(false);
    }
  };

  const updateChartLabel = (index, value) => {
    setChartLabels(prev => prev.map((l, i) => i === index ? value : l));
  };

  const updateChartValue = (index, value) => {
    setChartValues(prev => prev.map((v, i) => i === index ? (parseFloat(value) || 0) : v));
  };

  const addChartRow = () => {
    setChartLabels(prev => [...prev, '']);
    setChartValues(prev => [...prev, 0]);
  };

  const deleteChartRow = (index) => {
    if (chartLabels.length <= 1) return;
    setChartLabels(prev => prev.filter((_, i) => i !== index));
    setChartValues(prev => prev.filter((_, i) => i !== index));
  };

  const chartTypes = [
    { value: 'bar', label: 'Bar' },
    { value: 'line', label: 'Line' },
    { value: 'pie', label: 'Pie' },
    { value: 'doughnut', label: 'Doughnut' },
  ];

  const getCurrentEntityLabel = () => {
    if (entityType === 'organization' && selectedOrgId) {
      const org = organizations?.find(o => o.id === selectedOrgId);
      return org?.name || 'Organization';
    }
    const author = authors.find(a => a.id === selectedAuthorId);
    return author?.name || 'Select author';
  };

  // Is this a draft being edited?
  const isDraft = currentPost?.status === 'draft';
  const isEditing = currentPost && !isDraft;

  // Compute word count for footer
  const plainText = content.replace(/<[^>]*>/g, '');
  const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  // Get publish button label
  const getPublishLabel = () => {
    if (uploading) return 'Uploading...';
    if (publishing) return 'Publishing...';
    if (scheduleOn && scheduledAt) {
      const d = new Date(scheduledAt);
      return `Schedule · ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    if (isEditing) return 'Update';
    return 'Publish';
  };

  // ==========================================
  // DROPDOWN SUB-COMPONENT
  // ==========================================
  const ToolbarDropdown = ({ open, setOpen, value, placeholder, children }) => {
    const dropdownRef = useRef(null);

    useEffect(() => {
      if (!open) return;
      const handler = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open, setOpen]);

    return (
      <div ref={dropdownRef} style={{ position: 'relative', minWidth: 0, flexShrink: 1 }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
            backgroundColor: 'transparent', border: `1px solid ${t.borderStrong}`,
            borderRadius: '6px', color: value ? t.text : t.textSecondary,
            fontSize: '13px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
            whiteSpace: 'nowrap', transition: 'all 0.15s ease',
            maxWidth: '100%', overflow: 'hidden',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = t.borderStrong; }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{value || placeholder}</span>
          <ChevronDown size={13} />
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
            minWidth: '220px', maxHeight: '280px', overflowY: 'auto',
            backgroundColor: t.modalBg, border: `1px solid ${t.borderStrong}`,
            borderRadius: '8px', zIndex: 50,
            boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 16px 48px rgba(0,0,0,0.15)',
            padding: '4px',
          }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  // Icon button helper
  const IconBtn = ({ onClick, tooltip, active, children: icon }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <div style={{ position: 'relative' }}>
        <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
          style={{
            width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: active ? (isDark ? 'rgba(212,165,116,0.15)' : 'rgba(61,36,18,0.08)') : hovered ? (isDark ? 'rgba(245,230,200,0.06)' : 'rgba(61,36,18,0.04)') : 'transparent',
            border: 'none', borderRadius: '6px',
            color: active ? t.accent : hovered ? t.text : t.textSecondary,
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}
        >{icon}</button>
        {hovered && tooltip && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
            padding: '4px 8px', backgroundColor: t.modalBg, border: `1px solid ${t.borderStrong}`,
            borderRadius: '4px', fontSize: '11px', fontFamily: 'Poppins, sans-serif',
            color: t.textSecondary, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10,
          }}>{tooltip}</div>
        )}
      </div>
    );
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div style={{ minHeight: '100vh', fontFamily: "'Poppins', sans-serif" }}>
      {/* Hidden file inputs */}
      <input ref={featuredImageRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFeaturedImageSelect} style={{ display: 'none' }} />
      <input ref={inlineImageRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleInlineImageSelect} style={{ display: 'none' }} />

      {/* Publishing overlay */}
      {publishing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: t.overlayBg, backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: t.modalBg, borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
            <p style={{ fontSize: '16px', fontWeight: 500, color: t.text }}>Publishing your attested post...</p>
          </div>
        </div>
      )}

      {/* ========== TOP TOOLBAR ========== */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 30,
        backgroundColor: isDark ? 'rgba(17,11,7,0.9)' : 'rgba(253,251,247,0.9)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${t.border}`,
      }}>
        <div style={{
          maxWidth: '780px', margin: '0 auto', padding: '10px 24px',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {/* Category dropdown */}
          <ToolbarDropdown open={showCategoryDropdown} setOpen={setShowCategoryDropdown} value={category} placeholder="Category">
            {categories.length > 8 && (
              <input type="text" value={categorySearch} onChange={e => setCategorySearch(e.target.value)}
                placeholder="Search..." autoFocus
                style={{
                  width: '100%', padding: '8px 10px', backgroundColor: 'transparent',
                  border: 'none', borderBottom: `1px solid ${t.border}`,
                  color: t.text, fontSize: '13px', fontFamily: 'Poppins, sans-serif',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            )}
            {filteredCategories.map(cat => (
              <button key={cat} onClick={() => { setCategory(cat); setShowCategoryDropdown(false); setCategorySearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '9px 10px', textAlign: 'left',
                  backgroundColor: cat === category ? (isDark ? 'rgba(212,165,116,0.1)' : 'rgba(61,36,18,0.05)') : 'transparent',
                  border: 'none', color: cat === category ? t.text : t.textSecondary,
                  fontSize: '13px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
                  borderRadius: '4px',
                }}
                onMouseEnter={e => { if (cat !== category) e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'; }}
                onMouseLeave={e => { if (cat !== category) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {cat}
                {cat === category && <Check size={14} />}
              </button>
            ))}
          </ToolbarDropdown>

          {/* Author/Org dropdown */}
          <ToolbarDropdown open={showAuthorDropdown} setOpen={setShowAuthorDropdown} value={getCurrentEntityLabel()} placeholder="Post as...">

            {/* + New Author */}
            <button onClick={() => { setShowAuthorDropdown(false); setShowNewAuthorModal(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                width: '100%', padding: '9px 10px', textAlign: 'left',
                backgroundColor: 'transparent', border: 'none',
                color: t.accent, fontSize: '13px', fontWeight: 600,
                fontFamily: 'Poppins, sans-serif', cursor: 'pointer', borderRadius: '4px',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Plus size={14} /> New Author
            </button>

            {/* Authors */}
            {authors.map(a => (
              <button key={a.id} onClick={() => { handleEntitySelect(a.id); setShowAuthorDropdown(false); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '9px 10px', textAlign: 'left',
                  backgroundColor: (entityType === 'author' && selectedAuthorId === a.id) ? (isDark ? 'rgba(212,165,116,0.1)' : 'rgba(61,36,18,0.05)') : 'transparent',
                  border: 'none', color: t.textSecondary, fontSize: '13px',
                  fontFamily: 'Poppins, sans-serif', cursor: 'pointer', borderRadius: '4px',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'}
                onMouseLeave={e => { if (!(entityType === 'author' && selectedAuthorId === a.id)) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={12} /> {a.name}
                </span>
                {entityType === 'author' && selectedAuthorId === a.id && <Check size={14} />}
              </button>
            ))}

            {/* Organizations */}
            {organizations && organizations.length > 0 && (
              <>
                <div style={{ height: '1px', background: t.border, margin: '4px 0' }} />
                {organizations.map(org => {
                  const seat = orgSeats[org.id];
                  const linkedAuthor = seat?.author_id ? authors.find(a => a.id === seat.author_id) : null;
                  return (
                    <button key={org.id} onClick={() => { handleEntitySelect(`org_${org.id}`); setShowAuthorDropdown(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '9px 10px', textAlign: 'left',
                        backgroundColor: (entityType === 'organization' && selectedOrgId === org.id) ? (isDark ? 'rgba(212,165,116,0.1)' : 'rgba(61,36,18,0.05)') : 'transparent',
                        border: 'none', color: t.textSecondary, fontSize: '13px',
                        fontFamily: 'Poppins, sans-serif', cursor: 'pointer', borderRadius: '4px',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'}
                      onMouseLeave={e => { if (!(entityType === 'organization' && selectedOrgId === org.id)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Building2 size={12} />
                        {org.name}
                        {!linkedAuthor && <span style={{ fontSize: '10px', color: t.error }}>(link author)</span>}
                      </span>
                      {entityType === 'organization' && selectedOrgId === org.id && <Check size={14} />}
                    </button>
                  );
                })}
              </>
            )}
          </ToolbarDropdown>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Image upload icon */}
          <IconBtn
            tooltip={featuredImage ? 'Change cover image' : 'Add cover image'}
            active={!!featuredImage}
            onClick={() => featuredImageRef.current?.click()}
          >
            <Image size={18} />
          </IconBtn>

          {/* HTML import */}
          <HtmlImportButton onOpen={() => setShowImportModal(true)} />

          {/* Chart builder */}
          <ChartBuilderButton onOpen={() => setShowChartModal(true)} />

          {/* Drafts drawer toggle */}
          <IconBtn tooltip="Drafts" active={showDraftsPanel} onClick={toggleDraftsPanel}>
            <FileText size={18} />
          </IconBtn>

          {/* Schedule icon */}
          <div style={{ position: 'relative' }}>
            <IconBtn tooltip="Schedule" active={scheduleOn} onClick={() => setShowSchedulePicker(!showSchedulePicker)}>
              <Clock size={18} />
            </IconBtn>
            {showSchedulePicker && (
              <SchedulePicker
                scheduleOn={scheduleOn}
                scheduledAt={scheduledAt}
                setScheduleOn={setScheduleOn}
                setScheduledAt={setScheduledAt}
                onClose={() => setShowSchedulePicker(false)}
              />
            )}
          </div>
        </div>
      </div>

      {/* ========== EDITOR BODY ========== */}
      <div style={{ maxWidth: '780px', margin: '0 auto', padding: '40px 24px 120px' }}>

        {/* Scheduled post banner */}
        {currentPost && currentPost.scheduled_at && currentPost.scheduled_at > Date.now() && currentPost.status !== 'draft' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', borderRadius: 8, marginBottom: 24,
            background: isDark ? 'rgba(212,165,116,0.12)' : 'rgba(254,243,199,0.5)',
            border: `1px solid ${isDark ? 'rgba(212,165,116,0.3)' : '#fbbf24'}`,
            fontSize: 13, color: t.text,
          }}>
            <span>🕐</span>
            <span>
              This post is scheduled to publish on{' '}
              <strong>
                {new Date(currentPost.scheduled_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}{' '}
                at {new Date(currentPost.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </strong>.
              Edit freely — it won't go live until then.
            </span>
          </div>
        )}

        {/* Draft banner */}
        {isDraft && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', borderRadius: 8, marginBottom: 24,
            background: isDark ? 'rgba(245,230,200,0.06)' : 'rgba(61,36,18,0.03)',
            border: `1px solid ${t.borderStrong}`,
            fontSize: 13, color: t.textSecondary,
          }}>
            <Save size={14} />
            <span>Draft — not yet published. Click <strong>Publish</strong> when ready.</span>
          </div>
        )}

        {/* Featured image preview (only shown when image is set — upload via toolbar icon) */}
        {featuredImage && (
          <div style={{
            position: 'relative', width: '100%', height: '300px',
            borderRadius: '8px', overflow: 'hidden', marginBottom: '36px',
          }}>
            <img src={featuredImage} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.2) 100%)' }} />
            <button onClick={removeFeaturedImage}
              style={{
                position: 'absolute', top: '10px', right: '10px', width: '28px', height: '28px',
                borderRadius: '6px', backgroundColor: 'rgba(220,38,38,0.85)',
                border: 'none', color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(220,38,38,1)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(220,38,38,0.85)'; }}
            ><Trash2 size={14} /></button>
          </div>
        )}

        {/* Upload error */}
        {uploadError && (
          <p style={{ fontSize: '12px', color: t.error, marginBottom: '16px' }}>{uploadError}</p>
        )}

        {/* Title + Cancel/Publish row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '4px' }}>
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              flex: 1, fontSize: '42px', fontWeight: 700, fontFamily: "'Poppins', sans-serif",
              color: t.text, backgroundColor: 'transparent', border: 'none', outline: 'none',
              lineHeight: 1.15, letterSpacing: '-0.02em',
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', flexShrink: 0 }}>
            <button onClick={handleCancel}
              style={{
                padding: '8px 18px', backgroundColor: 'transparent',
                border: `1px solid ${t.borderStrong}`, borderRadius: '8px',
                color: t.textSecondary, fontSize: '13px', fontWeight: 500,
                fontFamily: 'Poppins, sans-serif', cursor: 'pointer', transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.error; e.currentTarget.style.color = t.error; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = t.borderStrong; e.currentTarget.style.color = t.textSecondary; }}
            >Cancel</button>

            <button
              onClick={savePost}
              disabled={!title.trim() || !content.trim() || uploading || publishing}
              style={{
                padding: '8px 22px',
                backgroundColor: (!title.trim() || !content.trim() || uploading || publishing) ? (isDark ? 'rgba(245,230,200,0.15)' : 'rgba(61,36,18,0.2)') : t.buttonBg,
                border: 'none', borderRadius: '8px',
                color: (!title.trim() || !content.trim() || uploading || publishing) ? t.textTertiary : t.buttonText,
                fontSize: '13px', fontWeight: 600, fontFamily: 'Poppins, sans-serif',
                cursor: (!title.trim() || !content.trim() || uploading || publishing) ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { if (title.trim() && content.trim() && !uploading && !publishing) e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >{getPublishLabel()}</button>
          </div>
        </div>

        {/* Subtitle */}
        <input
          type="text"
          placeholder="Add a subtitle..."
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          style={{
            width: '100%', fontSize: '20px', fontWeight: 300, fontFamily: "'Poppins', sans-serif",
            color: t.textSecondary, backgroundColor: 'transparent', border: 'none', outline: 'none',
            lineHeight: 1.4, marginBottom: '28px',
          }}
        />

        {/* Divider */}
        <div style={{ height: '1px', background: t.border, marginBottom: '28px' }} />

        {/* TipTap WYSIWYG Editor */}
        <QuillEditor ref={quillRef} content={content} setContent={setContent} />

        {/* Add Inline Image Button */}
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <button
            onClick={() => { setUploadMode('inline'); setShowImageUploadModal(true); }}
            disabled={uploading}
            style={{
              backgroundColor: 'transparent', border: `1px solid ${t.borderStrong}`,
              borderRadius: '6px', padding: '8px 16px', fontSize: '13px',
              cursor: uploading ? 'not-allowed' : 'pointer', color: t.text,
              fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center', gap: '8px',
              opacity: uploading ? 0.5 : 1, transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (!uploading) e.currentTarget.style.borderColor = t.accent; }}
            onMouseLeave={e => e.currentTarget.style.borderColor = t.borderStrong}
          >
            <Plus size={16} />
            {uploading ? 'Uploading...' : 'Add Image to Post'}
          </button>
        </div>

        {/* Word count footer */}
        {wordCount > 0 && (
          <div style={{
            marginTop: '20px', paddingTop: '12px',
            borderTop: `1px solid ${t.border}`,
            display: 'flex', gap: '16px', fontSize: '12px', color: t.textTertiary,
          }}>
            <span>{wordCount} words</span>
            <span>{readTime} min read</span>
          </div>
        )}
      </div>

      {/* Draft toast */}
      {draftToast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          padding: '10px 20px', backgroundColor: t.modalBg,
          border: `1px solid ${isDark ? 'rgba(74,222,128,0.3)' : 'rgba(22,163,74,0.3)'}`,
          borderRadius: '8px', color: t.success, fontSize: '13px', fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: '12px',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.15)',
          zIndex: 60, fontFamily: 'Poppins, sans-serif',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Check size={14} />
            Draft saved
          </span>
          <span style={{ width: '1px', height: '14px', backgroundColor: t.border }} />
          <button
            onClick={() => { setDraftToast(false); toggleDraftsPanel(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: t.accent, fontSize: '12px', fontWeight: 600,
              fontFamily: 'Poppins, sans-serif', textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >View drafts</button>
        </div>
      )}

      {/* ========== MODALS ========== */}

      {/* New Author Modal */}
      {showNewAuthorModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: t.overlayBg, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowNewAuthorModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: t.modalBg, borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: t.text }}>Create New Author</h2>
              <button onClick={() => setShowNewAuthorModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text }}><X size={24} /></button>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: t.text, fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>Author Name</label>
              <input type="text" value={newAuthorName} onChange={e => setNewAuthorName(e.target.value)} placeholder="Enter author name"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: `1px solid ${t.borderStrong}`, fontSize: '14px', fontFamily: 'Poppins, sans-serif', backgroundColor: t.surface, outline: 'none', color: t.text, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: t.text, fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>Bio (optional)</label>
              <textarea value={newAuthorBio} onChange={e => setNewAuthorBio(e.target.value)} placeholder="Short bio" rows={3}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: `1px solid ${t.borderStrong}`, fontSize: '14px', fontFamily: 'Poppins, sans-serif', backgroundColor: t.surface, outline: 'none', resize: 'vertical', color: t.text, boxSizing: 'border-box' }} />
            </div>
            <button onClick={createNewAuthor}
              style={{ width: '100%', backgroundColor: t.buttonBg, color: t.buttonText, padding: '12px', borderRadius: '12px', border: 'none', fontSize: '14px', fontWeight: '600', fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}>
              Create Author
            </button>
          </div>
        </div>
      )}

      {/* Link Author to Org Modal */}
      {showLinkAuthorModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: t.overlayBg, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { setShowLinkAuthorModal(false); setLinkingOrgId(null); }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: t.modalBg, borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%' }}>
            <h2 style={{ fontSize: '20px', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: t.text, marginBottom: '16px' }}>Link Author to Organization</h2>
            <p style={{ fontSize: '14px', color: t.textSecondary, marginBottom: '24px', fontFamily: 'Poppins, sans-serif', lineHeight: '1.6' }}>
              Select which author profile will represent you when posting as{' '}
              <strong>{organizations?.find(o => o.id === linkingOrgId)?.name}</strong>.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {authors.map(author => (
                <button key={author.id} onClick={() => linkAuthorToOrg(author.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '16px',
                    backgroundColor: t.surface, border: `1px solid ${t.borderStrong}`,
                    borderRadius: '8px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = t.borderStrong; }}
                >
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: t.buttonBg, color: t.buttonText, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '600', flexShrink: 0 }}>
                    {author.name[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, fontFamily: 'Poppins, sans-serif' }}>{author.name}</div>
                    {author.bio && <div style={{ fontSize: '12px', color: t.textSecondary, fontFamily: 'Poppins, sans-serif', marginTop: '2px' }}>{author.bio.substring(0, 50)}{author.bio.length > 50 ? '...' : ''}</div>}
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => { setShowLinkAuthorModal(false); setLinkingOrgId(null); }}
              style={{ width: '100%', marginTop: '16px', backgroundColor: 'transparent', color: t.textSecondary, padding: '12px', borderRadius: '6px', border: `1px solid ${t.borderStrong}`, fontSize: '14px', fontWeight: '500', fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      {showImageUploadModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: t.overlayBg, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowImageUploadModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: t.modalBg, borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: t.text }}>Add Image to Post</h2>
              <button onClick={() => setShowImageUploadModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text }}><X size={24} /></button>
            </div>
            <div
              onClick={() => inlineImageRef.current?.click()}
              style={{
                border: `2px dashed ${t.borderStrong}`, borderRadius: '8px', padding: '40px',
                textAlign: 'center', cursor: uploading ? 'not-allowed' : 'pointer',
                backgroundColor: t.surface, transition: 'all 0.2s', marginBottom: '16px',
              }}
              onMouseEnter={e => { if (!uploading) { e.currentTarget.style.borderColor = t.accent; } }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = t.borderStrong; }}
            >
              <Upload size={32} style={{ color: t.textSecondary, margin: '0 auto 12px' }} />
              <p style={{ fontSize: '14px', color: t.text, fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>
                {uploading ? 'Uploading...' : 'Click to select image'}
              </p>
              <p style={{ fontSize: '12px', color: t.textSecondary, fontFamily: 'Poppins, sans-serif', marginTop: '4px' }}>
                JPEG, PNG, GIF, or WebP (max 5MB)
              </p>
            </div>
            {uploadError && <p style={{ fontSize: '12px', color: t.error, fontFamily: 'Poppins, sans-serif', marginBottom: '16px', textAlign: 'center' }}>{uploadError}</p>}
            <button onClick={() => setShowImageUploadModal(false)}
              style={{ width: '100%', backgroundColor: 'transparent', color: t.textSecondary, padding: '12px', borderRadius: '6px', border: `1px solid ${t.borderStrong}`, fontSize: '14px', fontWeight: '500', fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cancel Confirm Popup */}
      {showCancelConfirm && (() => {
        const isDraftPost = currentPost?.status === 'draft';
        return (
        <div onClick={() => setShowCancelConfirm(false)} style={{
          position: 'fixed', inset: 0, backgroundColor: t.overlayBg,
          backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: t.modalBg, borderRadius: '12px', padding: '28px',
            width: '360px', maxWidth: '90vw',
            boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 16px 48px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, fontFamily: 'Poppins, sans-serif', color: t.text, margin: '0 0 8px 0' }}>
              {isDraftPost ? 'Save changes?' : 'Save as draft?'}
            </h3>
            <p style={{ fontSize: '13px', color: t.textSecondary, fontFamily: 'Poppins, sans-serif', margin: '0 0 24px 0', lineHeight: 1.5 }}>
              {isDraftPost
                ? 'You have unsaved changes to this draft. Save your work or discard the draft entirely.'
                : 'You have unsaved changes. Would you like to save this as a draft before leaving?'}
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={confirmSaveDraft} style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                backgroundColor: t.buttonBg, border: 'none',
                color: t.buttonText, fontSize: '13px', fontWeight: 600,
                fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >{isDraftPost ? 'Save Work' : 'Save Draft'}</button>
              <button onClick={confirmDiscard} style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                backgroundColor: 'transparent', border: `1px solid ${t.borderStrong}`,
                color: t.error, fontSize: '13px', fontWeight: 500,
                fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(248,113,113,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.borderStrong; }}
              >{isDraftPost ? 'Discard Draft' : 'Discard'}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Drafts Side Drawer */}
      {showDraftsPanel && (
        <>
          <div onClick={() => setShowDraftsPanel(false)} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 999,
          }} />
          <div ref={draftsPanelRef} style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: '340px', maxWidth: '85vw',
            backgroundColor: t.modalBg,
            borderLeft: `1px solid ${t.borderStrong}`,
            boxShadow: isDark ? '-8px 0 32px rgba(0,0,0,0.5)' : '-8px 0 32px rgba(0,0,0,0.1)',
            zIndex: 1000, display: 'flex', flexDirection: 'column',
            animation: 'slideInRight 0.2s ease',
          }}>
            <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

            {/* Drawer header */}
            <div style={{
              padding: '20px 20px 16px', borderBottom: `1px solid ${t.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: '16px', fontWeight: 600, color: t.text, fontFamily: 'Poppins, sans-serif' }}>Drafts</span>
              <button onClick={() => setShowDraftsPanel(false)} style={{
                background: 'none', border: 'none', color: t.textSecondary, cursor: 'pointer',
                display: 'flex', padding: '4px',
              }}><X size={16} /></button>
            </div>

            {/* Drawer body */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loadingDrafts ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: '13px', fontFamily: 'Poppins, sans-serif' }}>
                  Loading...
                </div>
              ) : drafts.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'Poppins, sans-serif' }}>
                  <FileText size={32} style={{ color: t.textTertiary, marginBottom: '12px' }} />
                  <p style={{ fontSize: '14px', color: t.textSecondary, margin: 0 }}>No drafts yet</p>
                  <p style={{ fontSize: '12px', color: t.textTertiary, margin: '4px 0 0' }}>Drafts are saved when you cancel a post</p>
                </div>
              ) : (
                drafts.map(draft => (
                  <div
                    key={draft.id}
                    onClick={() => loadDraft(draft.id)}
                    style={{
                      padding: '14px 20px', cursor: 'pointer',
                      borderBottom: `1px solid ${t.border}`,
                      transition: 'background 0.1s',
                      display: 'flex', alignItems: 'center', gap: '12px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        margin: 0, fontSize: '14px', fontWeight: 500, color: t.text,
                        fontFamily: 'Poppins, sans-serif',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {draft.title || 'Untitled'}
                      </p>
                      <p style={{ margin: '3px 0 0', fontSize: '12px', color: t.textSecondary, fontFamily: 'Poppins, sans-serif' }}>
                        {draft.category}{draft.author ? ` · ${draft.author}` : ''} · {new Date(draft.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => deleteDraft(draft.id, e)}
                      style={{
                        background: 'none', border: 'none', color: t.textTertiary,
                        cursor: 'pointer', display: 'flex', padding: '4px',
                        borderRadius: '4px', transition: 'all 0.15s', flexShrink: 0,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = t.error; }}
                      onMouseLeave={e => { e.currentTarget.style.color = t.textTertiary; }}
                    ><Trash2 size={14} /></button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* HTML Import Modal */}
      {showImportModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: t.overlayBg, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowImportModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: t.modalBg, borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: t.text }}>Import HTML</h2>
              <button onClick={() => setShowImportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text }}><X size={24} /></button>
            </div>
            <textarea
              value={importHtml}
              onChange={e => setImportHtml(e.target.value)}
              placeholder="Paste your HTML here..."
              rows={12}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: '8px',
                border: `1px solid ${t.borderStrong}`, fontSize: '14px',
                fontFamily: 'monospace', backgroundColor: t.surface,
                outline: 'none', resize: 'vertical', color: t.text,
                boxSizing: 'border-box', marginBottom: '16px',
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowImportModal(false)}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${t.borderStrong}`, borderRadius: '6px',
                  color: t.textSecondary, fontSize: '14px', fontWeight: '500',
                  fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleImportHtml}
                disabled={!importHtml.trim() || importing}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: (!importHtml.trim() || importing) ? (isDark ? 'rgba(245,230,200,0.15)' : 'rgba(61,36,18,0.2)') : t.buttonBg,
                  border: 'none', borderRadius: '12px',
                  color: (!importHtml.trim() || importing) ? t.textTertiary : t.buttonText,
                  fontSize: '14px', fontWeight: '600', fontFamily: 'Poppins, sans-serif',
                  cursor: (!importHtml.trim() || importing) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >{importing ? 'Importing...' : 'Import'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Chart Builder Modal */}
      {showChartModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: t.overlayBg, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowChartModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: t.modalBg, borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: t.text }}>Insert Chart</h2>
              <button onClick={() => setShowChartModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.text }}><X size={24} /></button>
            </div>

            {/* Chart type pills */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {chartTypes.map(ct => (
                <button
                  key={ct.value}
                  onClick={() => setChartType(ct.value)}
                  style={{
                    padding: '6px 16px', borderRadius: '999px',
                    fontSize: '13px', fontWeight: 500, fontFamily: 'Poppins, sans-serif',
                    cursor: 'pointer', transition: 'all 0.15s ease',
                    backgroundColor: chartType === ct.value ? '#3D2412' : 'transparent',
                    color: chartType === ct.value ? '#fef3c7' : t.textSecondary,
                    border: chartType === ct.value ? '1px solid #3D2412' : '1px solid #d6d3d1',
                  }}
                >{ct.label}</button>
              ))}
            </div>

            {/* Title */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: t.text, fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>Chart Title</label>
              <input
                type="text" value={chartTitle} onChange={e => setChartTitle(e.target.value)}
                placeholder="Optional title..."
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: `1px solid ${t.borderStrong}`, fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif', backgroundColor: t.surface,
                  outline: 'none', color: t.text, boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Data rows */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: t.text, fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>Data</label>
              {chartLabels.map((label, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                  <input
                    type="text" value={label} onChange={e => updateChartLabel(i, e.target.value)}
                    placeholder="Label"
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: '6px',
                      border: `1px solid ${t.borderStrong}`, fontSize: '13px',
                      fontFamily: 'Poppins, sans-serif', backgroundColor: t.surface,
                      outline: 'none', color: t.text, boxSizing: 'border-box',
                    }}
                  />
                  <input
                    type="number" value={chartValues[i]} onChange={e => updateChartValue(i, e.target.value)}
                    style={{
                      width: '80px', padding: '8px 10px', borderRadius: '6px',
                      border: `1px solid ${t.borderStrong}`, fontSize: '13px',
                      fontFamily: 'Poppins, sans-serif', backgroundColor: t.surface,
                      outline: 'none', color: t.text, boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={() => deleteChartRow(i)}
                    disabled={chartLabels.length <= 1}
                    style={{
                      width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'none', border: 'none', borderRadius: '4px',
                      color: chartLabels.length <= 1 ? t.textTertiary : t.textSecondary,
                      cursor: chartLabels.length <= 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s', flexShrink: 0,
                    }}
                  ><Trash2 size={14} /></button>
                </div>
              ))}
              <button
                onClick={addChartRow}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: t.accent, fontSize: '13px', fontWeight: 500,
                  fontFamily: 'Poppins, sans-serif', padding: '4px 0',
                }}
              ><Plus size={14} /> Add row</button>
            </div>

            {/* Color picker */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', color: t.text, fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>Color</label>
              <input
                type="color" value={chartColor} onChange={e => setChartColor(e.target.value)}
                style={{ width: '36px', height: '28px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent' }}
              />
            </div>

            {/* Live preview */}
            <div style={{
              height: '300px', marginBottom: '20px', borderRadius: '8px',
              border: `1px solid ${t.borderStrong}`, padding: '12px',
              backgroundColor: t.surface,
            }}>
              <canvas ref={chartCanvasRef} />
            </div>

            {/* Cancel / Insert */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowChartModal(false)}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${t.borderStrong}`, borderRadius: '6px',
                  color: t.textSecondary, fontSize: '14px', fontWeight: '500',
                  fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleInsertChart}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: t.buttonBg, border: 'none', borderRadius: '12px',
                  color: t.buttonText, fontSize: '14px', fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Attestation Modal */}
      {showAttestModal && pendingPostData && (
        <AttestModal
          postData={pendingPostData}
          entity={pendingEntity}
          onSuccess={handleAttestSuccess}
          onCancel={handleAttestCancel}
        />
      )}
    </div>
  );
}